
import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building, CheckCircle } from "lucide-react";
import { registerTenant, isUserRegistered, checkTenantExists } from "@/services/authService";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

const formSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters")
});

export default function RegistrationPage() {
  const { user, authStatus, refreshAuthStatus } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const [tenantExists, setTenantExists] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: ""
    }
  });

  // Check if the tenant already exists
  useEffect(() => {
    if (user && user.app_metadata?.tenant_id) {
      const tenantId = user.app_metadata.tenant_id;
      checkTenantExists(tenantId).then(exists => {
        setTenantExists(exists);
        if (exists) {
          // If tenant already exists, redirect to the error page
          navigate('/error');
        }
      });
    }
  }, [user, navigate]);

  // Check if user is already registered when component mounts
  useEffect(() => {
    if (user) {
      setIsCheckingRegistration(true);
      isUserRegistered(user.id).then(registered => {
        if (registered) {
          navigate('/');
        }
        setIsCheckingRegistration(false);
      });
    } else {
      setIsCheckingRegistration(false);
    }
  }, [user, navigate]);
  
  // If no user is logged in, redirect to signup
  if (!user) {
    return <Navigate to="/signup" />;
  }

  // If user is already registered, redirect to dashboard
  if (authStatus.isRegistered && !authStatus.isChecking && !isCheckingRegistration) {
    return <Navigate to="/" />;
  }

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) return;
    
    try {
      setIsRegistering(true);
      const success = await registerTenant(user, values.companyName);
      
      if (success) {
        await refreshAuthStatus();
        // Redirect to dashboard after successful registration
        navigate('/');
      } else {
        // If registration failed because tenant already has a master user
        navigate('/error');
      }
    } catch (error) {
      console.error("Registration error:", error);
      // In case of any error, redirect to error page
      navigate('/error');
    } finally {
      setIsRegistering(false);
    }
  };

  if (isCheckingRegistration) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="mt-4 text-2xl font-bold">Complete Registration</h1>
          <p className="text-center text-sm text-muted-foreground">
            You're almost done. Just tell us your organization name.
          </p>
        </div>
        
        <div className="space-y-4">
          <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
            <div className="flex">
              <div className="ml-0 flex-1 text-sm text-blue-800">
                <p>
                  <span className="font-medium">Microsoft account connected:</span> {user.email}
                </p>
                {user.app_metadata?.tenant_id && (
                  <p className="mt-1 text-xs">
                    Tenant ID: {user.app_metadata.tenant_id}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {tenantExists ? (
            <Alert variant="destructive">
              <AlertTitle>Registration Failed</AlertTitle>
              <AlertDescription>
                This organization already has an administrator account. Please contact your organization administrator for access.
              </AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                          <Input 
                            placeholder="Enter your organization name" 
                            className="pl-10" 
                            {...field} 
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isRegistering}
                >
                  {isRegistering ? "Registering..." : "Complete Registration"}
                </Button>
              </form>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}
